import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Button, Card, Form, Input, Row, Select, Tooltip, message } from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";
import axios from "axios";

import { AN_COLORS, AN_FONT, AN_MONO } from "../../../design-system/tokens";
import { AUTH_TOKEN, DOMAIN_ID, ORG_ID, REACT_APP_API } from "../../../constants";
import { createADPolicy, updateADPolicy } from "../../../service/adPolicyService";
import DatabasePolicy from "../../../components/policyEngine/ditConditions/basicInformation/databasePolicy";
import {
  DB_POLICY_FLOWS,
  DEFAULT_DB_POLICY_FLOW,
  normalizeDbPolicyFlow,
} from "./dbPolicyModel";

const { Option } = Select;

// Same generated-name shape the Grant Access shortcut uses (db-1234567-12345678).
function generatePolicyName() {
  const section = (len) =>
    Array.from({ length: len }, () => Math.floor(Math.random() * 10)).join("");
  return `db-${section(7)}-${section(8)}`;
}

function readDraft(isEditMode) {
  const raw = isEditMode
    ? localStorage.getItem("editPolicyData")
    : localStorage.getItem("policyDraft.basicPolicyInformation");
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

export default function CreateDbPolicyPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [form] = Form.useForm();
  const [messageApi, messageContext] = message.useMessage();

  const searchParams = new URLSearchParams(location.search);
  const isEditMode = searchParams.get("edit") === "true";

  const [submitting, setSubmitting] = useState(false);
  const [fieldMasking, setFieldMasking] = useState({});
  const [databaseType, setDatabaseType] = useState(null);
  const [iamUserOptions, setIamUserOptions] = useState([]);
  const [policyId, setPolicyId] = useState(null);

  // Prefill from the Grant Access draft or the policy being edited. The
  // embedded DatabasePolicy form prefills its own host/database/table fields
  // from the same localStorage keys.
  useEffect(() => {
    const statePolicy = location.state?.policy;
    const draft = statePolicy || readDraft(isEditMode);
    const db = draft?.policyJson?.database;
    if (db?.field_masking) setFieldMasking(db.field_masking);
    if (isEditMode) setPolicyId(draft?.id || draft?.policyId || null);
    form.setFieldsValue({
      policyName: draft?.policyName || draft?.policyJson?.policyName || generatePolicyName(),
      iamUsers: draft?.policyJson?.permissions?.iam_users || [],
      // A policy saved before the action existed comes back blank; normalize
      // reads that as Require MFA, which is what the backend fills in for it.
      // A value the engine wouldn't recognise normalizes to null and is left
      // unselected, so the admin has to make the choice rather than inherit a
      // silent default for a policy that is currently denying everything.
      policyFlow: normalizeDbPolicyFlow(draft?.policyJson?.policyFlow) || undefined,
    });
  }, [form, isEditMode, location.state]);

  useEffect(() => {
    const fetchIamUsers = async () => {
      try {
        const payload = {
          orgId: ORG_ID(),
          domainId: DOMAIN_ID(),
          pageNumber: 1,
          pageSize: 200,
          search: "",
          filter: "",
        };
        const res = await axios.post(`${REACT_APP_API}/api/v1/walletService/walletUserList`, payload, {
          headers: { withCredentials: true, "X-Authorization": AUTH_TOKEN() },
        });
        const users = res?.data?.users?.map((user) => ({
          value: user.emailAddress,
          label: user.emailAddress,
        }));
        setIamUserOptions(users || []);
      } catch (error) {
        messageApi.error("Failed to load users");
      }
    };
    fetchIamUsers();
  }, [messageApi]);

  const clearDrafts = () => {
    localStorage.removeItem("policyDraft.basicPolicyInformation");
    localStorage.removeItem("editPolicyData");
    localStorage.removeItem("formSessionData");
  };

  const handleFinish = (values) => {
    const policyName = values.policyName?.trim();
    // Same payload shape the original policy-engine wizard submitted for
    // database policies — the DB agent reads policyJson.database on checkout.
    const payload = {
      orgId: ORG_ID(),
      tenantId: DOMAIN_ID(),
      policyName,
      policyType: "database",
      generatedBy: "Manual",
      policyJson: {
        policyName,
        policyType: "database",
        // One of the three lowercase strings the engine accepts — never blank
        // and never omitted, so the admin sees the action they're getting
        // instead of the backend's default.
        policyFlow: normalizeDbPolicyFlow(values.policyFlow) || DEFAULT_DB_POLICY_FLOW,
        database: {
          database_name: values.databaseName,
          host: values.vm,
          tables: values.tables || [],
          // Field masking is not supported for Postgres — send null
          field_masking: databaseType?.toLowerCase().startsWith("postgres") ? null : fieldMasking || {},
          user: values.dbUser,
          privilege: values.dbPrivilege,
        },
        permissions: {
          allowed: true,
          iam_users: values.iamUsers || [],
          iam_groups: [],
        },
      },
    };

    setSubmitting(true);
    const onSuccess = (response) => {
      // Every update creates a new policy server-side; adopt the new id so a
      // follow-up update doesn't hit the now-"history" policy.
      const newPolicyId = response?.updatedPolicyID?.data;
      if (newPolicyId) {
        setPolicyId(newPolicyId);
        localStorage.setItem("storedDataId", newPolicyId);
      }
      messageApi.success(isEditMode ? "Policy updated successfully!" : "Policy created successfully!");
      clearDrafts();
      setSubmitting(false);
      navigate("/pam/policy/v2");
    };
    const onFailure = () => {
      messageApi.error(isEditMode ? "Failed to update policy" : "Failed to create policy");
      setSubmitting(false);
    };

    if (isEditMode && policyId) {
      updateADPolicy({
        payload: { ...payload, id: policyId, updatedBy: "admin_user" },
        onSuccess,
        onFailure,
      });
    } else {
      createADPolicy({ payload, onSuccess, onFailure });
    }
  };

  const handleCancel = () => {
    clearDrafts();
    navigate("/pam/policy/v2");
  };

  return (
    <div
      style={{
        minHeight: "calc(100vh - 64px)",
        background: AN_COLORS.bgWarm,
        color: AN_COLORS.ink,
        fontFamily: AN_FONT,
        padding: "28px 32px",
      }}
    >
      {messageContext}
      <div style={{ maxWidth: 760, margin: "0 auto" }}>
        {/* Header */}
        <div style={{ marginBottom: 24 }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              fontSize: 12.5,
              color: AN_COLORS.muted,
              marginBottom: 8,
            }}
          >
            <Link to="/pam/policy/v2" style={{ color: AN_COLORS.muted, textDecoration: "none" }}>
              Policies
            </Link>
            <span style={{ color: AN_COLORS.line }}>/</span>
            <span style={{ color: AN_COLORS.ink, fontWeight: 500 }}>
              {isEditMode ? "Edit" : "New"}
            </span>
          </div>
          <div
            style={{
              fontFamily: AN_MONO,
              fontSize: 10.5,
              letterSpacing: 1.4,
              textTransform: "uppercase",
              color: AN_COLORS.muted,
              marginBottom: 6,
            }}
          >
            Admin · Policies · Database
          </div>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 600, letterSpacing: -0.4 }}>
            {isEditMode ? "Edit database policy" : "Create database policy"}
          </h1>
          <div style={{ fontSize: 13, color: AN_COLORS.muted, marginTop: 4 }}>
            Grant a user access to a database with specific privileges. Access is enforced with MFA
            through the database agent.
          </div>
        </div>

        <Form form={form} layout="vertical" onFinish={handleFinish}>
          <Card title="Policy name" style={{ marginBottom: 16 }}>
            <Form.Item
              label="Policy name"
              name="policyName"
              rules={[{ required: true, message: "Please enter a policy name" }]}
            >
              <Input placeholder="db-access-policy" />
            </Form.Item>
          </Card>

          <Card
            title={
              <span>
                Who will get access (from your IAM)?{" "}
                <Tooltip title="Select the users who will be granted access through this policy.">
                  <InfoCircleOutlined />
                </Tooltip>
              </span>
            }
            style={{ marginBottom: 16 }}
          >
            <Form.Item
              label="Select IAM Users"
              name="iamUsers"
              rules={[{ required: true, message: "Please select at least one user" }]}
            >
              <Select
                mode="multiple"
                placeholder="Select users"
                disabled={!iamUserOptions.length}
                allowClear
              >
                {iamUserOptions.map((user) => (
                  <Option key={user.value} value={user.value}>
                    {user.label}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Card>

          <DatabasePolicy form={form} handleMaskingField={setFieldMasking} onDatabaseTypeChange={setDatabaseType} />

          {/* Applies to the policy as a whole, not to the database selection,
              so it gets its own section rather than a row inside the one above. */}
          <Card
            title={
              <span>
                What happens when they connect?{" "}
                <Tooltip title="What the database agent does when a login matches this policy.">
                  <InfoCircleOutlined />
                </Tooltip>
              </span>
            }
            style={{ marginBottom: 16 }}
          >
            <Form.Item
              label="Action"
              name="policyFlow"
              initialValue={DEFAULT_DB_POLICY_FLOW}
              rules={[{ required: true, message: "Please select an action" }]}
            >
              <Select placeholder="Select an action" optionLabelProp="label">
                {DB_POLICY_FLOWS.map((flow) => (
                  <Option key={flow.value} value={flow.value} label={flow.label}>
                    <div style={{ fontWeight: 500 }}>{flow.label}</div>
                    <div style={{ fontSize: 12, color: AN_COLORS.muted, whiteSpace: "normal" }}>
                      {flow.hint}
                    </div>
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Card>

          <Row justify="end" style={{ marginTop: 8 }}>
            <Button style={{ margin: "10px" }} onClick={handleCancel}>
              Cancel
            </Button>
            <Button
              style={{ margin: "10px" }}
              type="primary"
              htmlType="submit"
              loading={submitting}
            >
              {isEditMode ? "Update policy" : "Create policy"}
            </Button>
          </Row>
        </Form>
      </div>
    </div>
  );
}
